import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import * as api from '../api/research';
import type { PortalConfig, ProposalSummaryRow, ReadinessIssue } from '../api/types';
import type {
  CoInvestigator,
  CoverLetterState,
  ProposalData,
  ProposalStatus,
  Researcher,
  UploadedDocument
} from '../types/portal';
import { INSTITUTION } from '../data/proposalContent';
import {
  fitMonths,
  fromProposalBody,
  ganttMonthCount,
  monthsFromRow,
  toSaveProposalBody
} from '../data/fieldMap';
import { WIZARD_STEPS } from '../data/wizardSteps';
import type { CoverLetterRow } from '../api/types';

const SESSION_KEY = 'smdc-irb-session';
const AGREED_KEY = 'smdc-irb-declared';
const OPEN_PROPOSAL_KEY = 'smdc-irb-open-proposal';

export const emptyProposal: ProposalData = {
  title: '',
  reviewCategory: '',
  projectTypes: [],
  startDate: '',
  durationMonths: '',
  narratives: {},
  gantt: [],
  references: '',
  facilities: '',
  potentialBenefits: '',
  potentialRisks: '',
  ethics: '',
  dissemination: '',
  grantRequired: '',
  grantAmount: '',
  piName: '',
  piTitle: '',
  piInstitution: INSTITUTION,
  piContact: '',
  piEmail: '',
  coInvestigators: [],
  hodName: '',
  supervisorName: '',
  documents: {},
  coverLetter: {
    required: false,
    statusId: '',
    uploaded: false,
    fileName: '',
    uploadedOn: null,
    hasObservationLetter: false
  },
  agreed: false
};

function toCoverLetter(row: CoverLetterRow | null | undefined): CoverLetterState {
  if (!row) return { ...emptyProposal.coverLetter };
  return {
    required: row.IsRequired === true,
    statusId: row.StatusId ?? '',
    uploaded: row.IsUploaded === true,
    fileName: row.OriginalFileName ?? '',
    uploadedOn: row.UploadedAt,
    hasObservationLetter: row.HasObservationLetter === true
  };
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface PortalContextValue {
  /* session */
  sessionToken: string | null;
  user: Researcher | null;
  config: PortalConfig | null;
  booting: boolean;

  /* every proposal this researcher has, newest first */
  proposals: ProposalSummaryRow[];

  /* the proposal being worked on */
  data: ProposalData;
  status: ProposalStatus | null;
  history: api.FullProposal['history'];
  loadingProposal: boolean;

  /* wizard bookkeeping */
  completedSteps: string[];
  /** False for steps the WebForms wizard never made compulsory. */
  isRequired: (stepId: string) => boolean;
  isSatisfied: (stepId: string) => boolean;
  markComplete: (stepId: string) => void;

  /* save state */
  saveState: SaveState;
  lastSaved: string | null;
  saveError: string | null;

  /* actions */
  adoptSession: (token: string, profile: Researcher) => void;
  signOut: () => Promise<void>;
  update: (patch: Partial<ProposalData>) => void;
  setNarrative: (key: string, value: string) => void;
  save: () => Promise<boolean>;
  reloadProposal: () => Promise<void>;
  ensureDraft: () => Promise<boolean>;
  /** Opens one of the researcher's existing proposals. */
  selectProposal: (researchCode: number) => Promise<boolean>;
  /** Starts a fresh proposal and opens it. Returns its code, or 0 on failure. */
  startNewProposal: () => Promise<number>;
  uploadDocument: (docCode: number, file: File) => Promise<string | null>;
  removeDocument: (docCode: number) => Promise<string | null>;
  uploadCoverLetter: (file: File) => Promise<string | null>;
  checkReadiness: () => Promise<{ isReady: boolean; issues: ReadinessIssue[] }>;
  submit: () => Promise<{ ok: boolean; message: string; documentProblems: string[] }>;
}

const PortalContext = createContext<PortalContextValue | null>(null);

function readStoredSession(): string | null {
  try {
    return window.localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

function writeStoredSession(token: string | null) {
  try {
    if (token) window.localStorage.setItem(SESSION_KEY, token);
    else window.localStorage.removeItem(SESSION_KEY);
  } catch {
    /* private browsing — the session simply will not survive a reload */
  }
}

/**
 * The declaration tick is the researcher's own affirmation and is only sent at
 * submission, so there is nowhere on the server to keep it until then. Holding
 * it per proposal on the device means a refresh between ticking the box and
 * pressing Submit does not silently undo it.
 */
function readAgreed(researchCode: number): boolean {
  if (!researchCode) return false;
  try {
    return window.localStorage.getItem(`${AGREED_KEY}:${researchCode}`) === '1';
  } catch {
    return false;
  }
}

function writeAgreed(researchCode: number, agreed: boolean) {
  if (!researchCode) return;
  try {
    if (agreed) window.localStorage.setItem(`${AGREED_KEY}:${researchCode}`, '1');
    else window.localStorage.removeItem(`${AGREED_KEY}:${researchCode}`);
  } catch {
    /* ignore */
  }
}

/**
 * Which proposal the researcher had open. A researcher accumulates proposals
 * over the years, so "the one I was working on" has to survive a reload rather
 * than silently snapping back to the newest.
 */
function readOpenProposal(): number {
  try {
    return Number(window.localStorage.getItem(OPEN_PROPOSAL_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeOpenProposal(researchCode: number) {
  try {
    if (researchCode) window.localStorage.setItem(OPEN_PROPOSAL_KEY, String(researchCode));
    else window.localStorage.removeItem(OPEN_PROPOSAL_KEY);
  } catch {
    /* ignore */
  }
}

export function PortalProvider({ children }: { children: React.ReactNode }) {
  const [sessionToken, setSessionToken] = useState<string | null>(readStoredSession);
  const [user, setUser] = useState<Researcher | null>(null);
  const [config, setConfig] = useState<PortalConfig | null>(null);
  const [booting, setBooting] = useState(true);

  const [proposals, setProposals] = useState<ProposalSummaryRow[]>([]);
  const [data, setData] = useState<ProposalData>(emptyProposal);
  const [status, setStatus] = useState<ProposalStatus | null>(null);
  const [history, setHistory] = useState<api.FullProposal['history']>([]);
  const [loadingProposal, setLoadingProposal] = useState(false);

  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const researchCode = useRef<number>(0);
  const sessionTokenRef = useRef<string | null>(sessionToken);
  sessionTokenRef.current = sessionToken;
  const dirty = useRef(false);
  const dataRef = useRef(data);
  dataRef.current = data;

  /* ------------------------------------------------------------ bootstrap */

  const applyProposal = useCallback((proposal: api.FullProposal) => {
    const header = proposal.header;
    const body = proposal.body;
    const submission = proposal.submission;

    researchCode.current = header?.ResearchCode ?? 0;
    writeOpenProposal(researchCode.current);

    const months = ganttMonthCount(body?.DurationMonths ?? '');

    const next: ProposalData = {
      ...fromProposalBody(body, emptyProposal),
      projectTypes: proposal.typeIds,
      gantt: proposal.gantt.map((row, index) => ({
        id: `g${row.ActivityID ?? index}`,
        activity: String(row.Activity ?? ''),
        months: monthsFromRow(row as Record<string, unknown>, months)
      })),
      grantRequired: submission ? (submission.GrantRequired ? 'yes' : 'no') : '',
      grantAmount: submission && submission.GrantAmount > 0 ? String(submission.GrantAmount) : '',
      piName: submission?.PiName ?? '',
      piTitle: submission?.PiTitle ?? '',
      piInstitution: submission?.PiInstitution || INSTITUTION,
      piContact: submission?.PiContact ?? '',
      piEmail: submission?.PiEmail ?? '',
      hodName: submission?.HodName ?? '',
      supervisorName: submission?.SupervisorName ?? '',
      coInvestigators: proposal.coInvestigators.map((row) => ({
        id: `ci${row.CoInvestigatorID}`,
        name: row.Name,
        title: row.Title,
        department: row.Department,
        contact: row.Contact
      })),
      documents: Object.fromEntries(
        proposal.documents.map((doc) => [
          doc.DocCode,
          doc.IsUploaded
            ? ({ fileName: doc.FileName, uploadedOn: doc.LastUploadedOn } as UploadedDocument)
            : null
        ])
      ),
      coverLetter: toCoverLetter(proposal.coverLetter),
      /* A submitted proposal was necessarily declared; a draft remembers the
         tick the researcher already made. */
      agreed: header?.IsSubmitted === true || readAgreed(header?.ResearchCode ?? 0)
    };

    setData(next);
    setHistory(proposal.history);
    setStatus(
      header
        ? {
            researchCode: header.ResearchCode,
            irbNumber: header.IrbNumber,
            statusId: header.StatusId,
            statusDescription: header.StatusDescription,
            statusForResearcher: header.StatusForResearcher,
            isSubmitted: header.IsSubmitted,
            submittedAt: header.SubmitDateTime,
            revisions: header.Revisions,
            canEdit: proposal.canEdit,
            fileName: header.FileName ?? ''
          }
        : null
    );

    /* Sections that already hold content count as done, so the timeline is
       meaningful when a researcher returns to a draft on another device. */
    setCompletedSteps(derivedCompleteSteps(next));
    dirty.current = false;
  }, []);

  /**
   * Loads whatever proposal the researcher already has. It deliberately does
   * not create one: committee and reviewer accounts sign in through the same
   * provider, and an empty draft per staff login would quietly fill the
   * proposal table with rows nobody ever submits. Drafts are created on demand
   * by ensureDraft, from the wizard.
   */
  const loadProposal = useCallback(
    async (token: string) => {
      setLoadingProposal(true);
      try {
        const list = await api.myProposals(token);
        if (!list.ok) return;

        setProposals(list.rows);
        if (list.rows.length === 0) return;

        /* Prefer whatever was open last, then an unsubmitted draft, then the
           newest. Without the first of those, a researcher reviewing an older
           proposal would be bounced away from it on every reload. */
        const remembered = readOpenProposal();
        const open =
          list.rows.find((row) => row.ResearchCode === remembered) ??
          list.rows.find((row) => !row.IsSubmitted) ??
          list.rows[0];

        const detail = await api.getProposal(token, open.ResearchCode);
        if (detail.ok) applyProposal(detail.proposal);
      } finally {
        setLoadingProposal(false);
      }
    },
    [applyProposal]
  );

  const refreshProposals = useCallback(async () => {
    const token = sessionTokenRef.current;
    if (!token) return;
    const list = await api.myProposals(token);
    if (list.ok) setProposals(list.rows);
  }, []);

  /** Opens an existing proposal of this researcher's. */
  const selectProposal = useCallback(
    async (code: number): Promise<boolean> => {
      const token = sessionTokenRef.current;
      if (!token || !code) return false;
      if (researchCode.current === code) return true;

      setLoadingProposal(true);
      try {
        const detail = await api.getProposal(token, code);
        if (!detail.ok) return false;
        applyProposal(detail.proposal);
        return true;
      } finally {
        setLoadingProposal(false);
      }
    },
    [applyProposal]
  );

  /**
   * Starts a fresh proposal. The procedure reopens an existing unsubmitted
   * draft rather than piling up empties, so this is safe to press twice.
   */
  const startNewProposal = useCallback(async (): Promise<number> => {
    const token = sessionTokenRef.current;
    if (!token) return 0;

    setLoadingProposal(true);
    try {
      const started = await api.startProposal(token);
      if (!started.ok || !started.researchCode) return 0;

      const detail = await api.getProposal(token, started.researchCode);
      if (detail.ok) applyProposal(detail.proposal);

      await refreshProposals();
      return started.researchCode;
    } finally {
      setLoadingProposal(false);
    }
  }, [applyProposal, refreshProposals]);

  /** Creates the researcher's draft the first time they actually need one. */
  const ensureDraft = useCallback(async (): Promise<boolean> => {
    const token = sessionToken;
    if (!token) return false;
    if (researchCode.current) return true;

    setLoadingProposal(true);
    try {
      const started = await api.startProposal(token);
      if (!started.ok) return false;

      const detail = await api.getProposal(token, started.researchCode);
      if (!detail.ok) return false;

      applyProposal(detail.proposal);
      return true;
    } finally {
      setLoadingProposal(false);
    }
  }, [applyProposal, sessionToken]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const cfg = await api.fetchConfig();
        if (!cancelled) setConfig(cfg);
      } catch {
        /* the sign-in screen copes without it, minus the Turnstile widget */
      }

      const token = readStoredSession();
      if (token) {
        try {
          const session = await api.validateSession(token);
          if (!cancelled && session.ok && session.profile) {
            setSessionToken(token);
            setUser(toResearcher(session.profile));
            await loadProposal(token);
          } else if (!cancelled) {
            writeStoredSession(null);
            setSessionToken(null);
          }
        } catch {
          /* offline — leave the token be and let the next call decide */
        }
      }

      if (!cancelled) setBooting(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [loadProposal]);

  /* --------------------------------------------------------------- session */

  const adoptSession = useCallback(
    (token: string, profile: Researcher) => {
      writeStoredSession(token);
      setSessionToken(token);
      setUser(profile);
      void loadProposal(token);
    },
    [loadProposal]
  );

  const signOut = useCallback(async () => {
    const token = sessionToken;
    writeStoredSession(null);
    setSessionToken(null);
    setUser(null);
    setData(emptyProposal);
    setStatus(null);
    setHistory([]);
    setCompletedSteps([]);
    setProposals([]);
    researchCode.current = 0;
    writeOpenProposal(0);
    if (token) {
      try {
        await api.logout(token);
      } catch {
        /* the local session is gone either way */
      }
    }
  }, [sessionToken]);

  /* ----------------------------------------------------------------- edits */

  const update = useCallback((patch: Partial<ProposalData>) => {
    if (patch.agreed !== undefined) writeAgreed(researchCode.current, patch.agreed);
    dirty.current = true;
    setSaveState('idle');
    setData((prev) => {
      const next = { ...prev, ...patch };
      /* Stretching the study also stretches the Gantt grid. */
      if (patch.durationMonths !== undefined) {
        const count = ganttMonthCount(next.durationMonths);
        next.gantt = next.gantt.map((row) => ({ ...row, months: fitMonths(row.months, count) }));
      }
      return next;
    });
  }, []);

  const setNarrative = useCallback((key: string, value: string) => {
    dirty.current = true;
    setSaveState('idle');
    setData((prev) => ({ ...prev, narratives: { ...prev.narratives, [key]: value } }));
  }, []);

  /* ------------------------------------------------------------------ save */

  const save = useCallback(async (): Promise<boolean> => {
    const token = sessionToken;
    const code = researchCode.current;
    if (!token || !code) return false;
    if (status && !status.canEdit) return false;

    const current = dataRef.current;
    setSaveState('saving');
    setSaveError(null);

    try {
      const proposal = await api.saveProposal({
        SessionToken: token,
        ResearchCode: code,
        ...toSaveProposalBody(current)
      });
      if (!proposal.ok) {
        setSaveState('error');
        setSaveError(proposal.message || 'Could not save.');
        return false;
      }

      const gantt = await api.saveGantt({
        SessionToken: token,
        ResearchCode: code,
        Activities: JSON.stringify(
          current.gantt
            .filter((row) => row.activity.trim())
            .map((row) => ({ activity: row.activity, months: row.months }))
        )
      });
      if (!gantt.ok) {
        setSaveState('error');
        setSaveError(gantt.message || 'Could not save the time frame.');
        return false;
      }

      const submission = await api.saveSubmission({
        SessionToken: token,
        ResearchCode: code,
        GrantRequired: current.grantRequired === '' ? null : current.grantRequired === 'yes',
        GrantAmount: current.grantAmount ? Number(current.grantAmount) : null,
        PiName: current.piName,
        PiTitle: current.piTitle,
        PiInstitution: current.piInstitution,
        PiContact: current.piContact,
        PiEmail: current.piEmail,
        HodName: current.hodName,
        SupervisorName: current.supervisorName,
        CoInvestigators: JSON.stringify(
          current.coInvestigators.map((ci: CoInvestigator) => ({
            name: ci.name,
            title: ci.title,
            department: ci.department,
            contact: ci.contact
          }))
        )
      });
      if (!submission.ok) {
        setSaveState('error');
        setSaveError(submission.message || 'Could not save.');
        return false;
      }

      dirty.current = false;
      setSaveState('saved');
      setLastSaved(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      return true;
    } catch (error) {
      setSaveState('error');
      setSaveError(error instanceof Error ? error.message : 'Could not save.');
      return false;
    }
  }, [sessionToken, status]);

  /* Autosave, quietly, so a closed tab does not cost the researcher an hour. */
  useEffect(() => {
    if (!dirty.current || !sessionToken || !researchCode.current) return;
    if (status && !status.canEdit) return;

    const timer = window.setTimeout(() => {
      void save();
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [data, save, sessionToken, status]);

  const reloadProposal = useCallback(async () => {
    const token = sessionToken;
    const code = researchCode.current;
    if (!token || !code) return;
    const detail = await api.getProposal(token, code);
    if (detail.ok) applyProposal(detail.proposal);
  }, [applyProposal, sessionToken]);

  /* ------------------------------------------------------------- documents */

  const uploadDocument = useCallback(
    async (docCode: number, file: File): Promise<string | null> => {
      const token = sessionToken;
      const code = researchCode.current;
      if (!token || !code) return 'Your session has expired. Please sign in again.';

      try {
        const res = await api.uploadDocument(token, code, docCode, file);
        if (!res.ok) return res.message || 'The upload was refused.';

        setData((prev) => ({
          ...prev,
          documents: {
            ...prev.documents,
            [docCode]: {
              fileName: String(res.head.OriginalFileName ?? res.head.FileName ?? file.name),
              uploadedOn: new Date().toISOString()
            }
          }
        }));
        return null;
      } catch (error) {
        return error instanceof Error ? error.message : 'The upload failed.';
      }
    },
    [sessionToken]
  );

  const removeDocument = useCallback(
    async (docCode: number): Promise<string | null> => {
      const token = sessionToken;
      const code = researchCode.current;
      if (!token || !code) return 'Your session has expired. Please sign in again.';

      try {
        const res = await api.deleteDocument(token, code, docCode);
        if (!res.ok) return res.message || 'Could not remove the document.';
        setData((prev) => ({ ...prev, documents: { ...prev.documents, [docCode]: null } }));
        return null;
      } catch (error) {
        return error instanceof Error ? error.message : 'Could not remove the document.';
      }
    },
    [sessionToken]
  );

  const uploadCoverLetter = useCallback(
    async (file: File): Promise<string | null> => {
      const token = sessionToken;
      const code = researchCode.current;
      if (!token || !code) return 'Your session has expired. Please sign in again.';

      try {
        const res = await api.uploadCoverLetter(token, code, file);
        if (!res.ok) return res.message || 'The upload was refused.';

        setData((prev) => ({
          ...prev,
          coverLetter: {
            ...prev.coverLetter,
            uploaded: true,
            fileName: String(res.head.OriginalFileName ?? file.name),
            uploadedOn: new Date().toISOString()
          }
        }));
        return null;
      } catch (error) {
        return error instanceof Error ? error.message : 'The upload failed.';
      }
    },
    [sessionToken]
  );

  /* ---------------------------------------------------------------- submit */

  const checkReadiness = useCallback(async () => {
    const token = sessionToken;
    const code = researchCode.current;
    if (!token || !code) return { isReady: false, issues: [] as ReadinessIssue[] };

    const res = await api.checkReadiness(token, code);
    return { isReady: res.isReady, issues: res.issues };
  }, [sessionToken]);

  const submit = useCallback(async () => {
    const token = sessionToken;
    const code = researchCode.current;
    if (!token || !code) {
      return {
        ok: false,
        message: 'Your session has expired. Please sign in again.',
        documentProblems: [] as string[]
      };
    }

    /* Never submit a stale copy. */
    const saved = await save();
    if (!saved && dirty.current) {
      return {
        ok: false,
        message: saveError ?? 'Save your changes before submitting.',
        documentProblems: [] as string[]
      };
    }

    const res = await api.submitProposal(token, code);

    /* The API builds the submission document before recording anything, so a
       failure here means nothing was submitted — but the IRB number it reserved
       is now on the proposal, which the reloaded status will show. */
    await reloadProposal();
    await refreshProposals();

    return { ok: res.ok, message: res.message, documentProblems: res.documentProblems };
  }, [refreshProposals, reloadProposal, save, saveError, sessionToken]);

  /* -------------------------------------------------------- wizard helpers */

  const isRequired = useCallback((stepId: string) => {
    const step = WIZARD_STEPS.find((s) => s.id === stepId);
    return !step?.optional;
  }, []);

  const isSatisfied = useCallback(
    (stepId: string) => completedSteps.includes(stepId) || !isRequired(stepId),
    [completedSteps, isRequired]
  );

  const markComplete = useCallback((stepId: string) => {
    setCompletedSteps((prev) => (prev.includes(stepId) ? prev : [...prev, stepId]));
  }, []);

  const value = useMemo<PortalContextValue>(
    () => ({
      sessionToken,
      user,
      config,
      booting,
      proposals,
      data,
      status,
      history,
      loadingProposal,
      completedSteps,
      isRequired,
      isSatisfied,
      markComplete,
      saveState,
      lastSaved,
      saveError,
      adoptSession,
      signOut,
      update,
      setNarrative,
      save,
      reloadProposal,
      ensureDraft,
      selectProposal,
      startNewProposal,
      uploadDocument,
      removeDocument,
      uploadCoverLetter,
      checkReadiness,
      submit
    }),
    [
      sessionToken,
      user,
      config,
      booting,
      proposals,
      data,
      status,
      history,
      loadingProposal,
      completedSteps,
      isRequired,
      isSatisfied,
      markComplete,
      saveState,
      lastSaved,
      saveError,
      adoptSession,
      signOut,
      update,
      setNarrative,
      save,
      reloadProposal,
      ensureDraft,
      selectProposal,
      startNewProposal,
      uploadDocument,
      removeDocument,
      uploadCoverLetter,
      checkReadiness,
      submit
    ]
  );

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

export function usePortal(): PortalContextValue {
  const ctx = useContext(PortalContext);
  if (!ctx) throw new Error('usePortal must be used inside PortalProvider');
  return ctx;
}

export function toResearcher(profile: {
  ResearcherID: number;
  FullName: string;
  Email: string;
  Contact: string;
  Designation: string;
  Institution: string;
  IsSrcAdmin: boolean;
  IsIrbAdmin: boolean;
  IsReviewer: boolean;
}): Researcher {
  return {
    researcherId: profile.ResearcherID,
    fullName: profile.FullName,
    email: profile.Email,
    contact: profile.Contact,
    designation: profile.Designation || 'Principal Investigator',
    institution: profile.Institution || INSTITUTION,
    isSrcAdmin: profile.IsSrcAdmin,
    isIrbAdmin: profile.IsIrbAdmin,
    isReviewer: profile.IsReviewer
  };
}

/** Which wizard steps already hold enough content to count as visited. */
function derivedCompleteSteps(data: ProposalData): string[] {
  const done: string[] = [];
  const has = (v: string | undefined) => Boolean(v && v.trim());

  WIZARD_STEPS.forEach((step) => {
    switch (step.kind) {
      case 'identification':
        if (has(data.title)) done.push(step.id);
        break;
      case 'projectTypes':
        if (data.projectTypes.length > 0) done.push(step.id);
        break;
      case 'studyPeriod':
        if (has(data.startDate) && has(data.durationMonths)) done.push(step.id);
        break;
      case 'text': {
        const areas = step.textareas ?? [];
        const filled = areas.every((area) =>
          has(
            area.source === 'narrative'
              ? data.narratives[area.key]
              : (data as unknown as Record<string, string>)[area.key]
          )
        );
        if (areas.length > 0 && filled) done.push(step.id);
        break;
      }
      case 'gantt':
        if (data.gantt.some((row) => row.activity.trim())) done.push(step.id);
        break;
      case 'grant':
        if (data.grantRequired) done.push(step.id);
        break;
      case 'principalInvestigator':
        if (has(data.piName) && has(data.piEmail)) done.push(step.id);
        break;
      case 'coInvestigators':
        if (data.coInvestigators.length > 0) done.push(step.id);
        break;
      case 'endorsements':
        if (has(data.hodName)) done.push(step.id);
        break;
      case 'documents':
        if (
          Object.values(data.documents).some(Boolean) &&
          (!data.coverLetter.required || data.coverLetter.uploaded)
        )
          done.push(step.id);
        break;
      case 'declaration':
        if (data.agreed) done.push(step.id);
        break;
      default:
        break;
    }
  });

  return done;
}

